import { format, parseISO } from 'date-fns';
import { type FormApi } from 'final-form';
import { omitBy, pick } from 'lodash';
import { useMemo } from 'react';

import Card from 'components/Card';
import UpdateGroupRequestForm, {
  FormValues,
} from 'components/UpdateGroupRequestForm';
import UserBadge from 'components/UserBadge';
import {
  RequiredFieldInput,
  RosterRequestDataNewGroupReservation,
  RosterRequestQuery,
  RosterRequestStatus,
  useUpdateGroupReservationRequestMutation,
  ClientTeamMemberSelectOptionInput,
  ClientTeamMemberSelectOption,
  useCreateRosterRequestRemarkMutation,
} from 'generated/graphql';
import { ClientTeamMemberOption } from 'hooks/useClientClientTeamMemberSelector';
import { useUserRole } from 'hooks/useUserRole';
import { popToast } from 'utilities/popToast';
import removeTypename from 'utilities/removeTypename';

import ConfirmedBooking from './GroupBookingDetails/ConfirmedBooking';
import TravelerAssignmentView from './GroupBookingDetails/TravelerAssignmentView';
import Unconfirmed from './GroupBookingDetails/Unconfirmed';
import YourGroupRequestCard from './GroupBookingDetails/YourGroupRequestCard';
import RequestRemarksForm, {
  RequestRemarksFormProps,
} from './RequestDetails/RequestRemarksForm';

type RequestedChangesProps = {
  requestData: RosterRequestDataNewGroupReservation;
  requestStatus: RosterRequestStatus;
  id: string;
  request?: RosterRequestQuery['rosterRequest'];
};

const ALLOWED_CLIENT_TEAM_MEMBER_FIELDS = [
  'id',
  'firstName',
  'lastName',
  'gender',
  'rank',
  'edipi',
  'mission',
  'taskForce',
  'employeeNumber',
  'jobTitle',
] as const;

const toClientTeamMemberInput = (
  member: ClientTeamMemberSelectOption | ClientTeamMemberOption,
): ClientTeamMemberSelectOptionInput => {
  const picked = pick(member, ALLOWED_CLIENT_TEAM_MEMBER_FIELDS);
  return omitBy(picked, (v) => v === null) as ClientTeamMemberSelectOptionInput;
};

const NewGroupReservationRequest = ({
  id,
  requestData,
  requestStatus,
  request,
}: RequestedChangesProps) => {
  const { isOnlyTraveler } = useUserRole();

  // Helper to group flat guest array into room objects based on configurations
  const groupGuestsByRoomConfiguration = (
    flatGuestArray: ClientTeamMemberSelectOption[],
    configurations?: Array<{ rooms: number; guests: number }>,
  ): (
    | ClientTeamMemberSelectOptionInput
    | Record<string, ClientTeamMemberSelectOptionInput>
  )[] => {
    if (!configurations || configurations.length === 0) {
      return flatGuestArray.map(toClientTeamMemberInput);
    }

    const result: (
      | ClientTeamMemberSelectOptionInput
      | Record<string, ClientTeamMemberSelectOptionInput>
    )[] = [];
    let guestIndex = 0;

    for (const config of configurations) {
      for (let roomIndex = 0; roomIndex < config.rooms; roomIndex++) {
        if (config.guests === 1) {
          // Single guest - flat structure
          if (guestIndex < flatGuestArray.length) {
            result.push(toClientTeamMemberInput(flatGuestArray[guestIndex]));
            guestIndex++;
          }
        } else {
          // Multiple guests - nested object with guest0, guest1, etc.
          const roomGuests: Record<string, ClientTeamMemberSelectOptionInput> =
            {};
          for (let i = 0; i < config.guests; i++) {
            if (guestIndex < flatGuestArray.length) {
              roomGuests[`guest${i}`] = toClientTeamMemberInput(
                flatGuestArray[guestIndex],
              );
              guestIndex++;
            }
          }
          result.push(roomGuests);
        }
      }
    }

    return result;
  };

  const { single, double, suite } = useMemo(() => {
    const { single, double, suite, roomConfigurations } = requestData;

    // Group single rooms based on configurations
    const mappedSingle = groupGuestsByRoomConfiguration(
      single,
      roomConfigurations?.single,
    );

    // Group suite rooms based on configurations
    const mappedSuite = groupGuestsByRoomConfiguration(
      suite,
      roomConfigurations?.suite,
    );

    // Map double rooms - flatten to guest if config says 1 guest
    const mappedDouble = double.map((room, index) => {
      const { firstBed, secondBed } = room;

      // Check if this double room has a 1-guest configuration
      const doubleConfigs = roomConfigurations?.double || [];
      let roomConfigIndex = 0;
      let guestsForThisRoom = 2; // Default to 2 guests

      // Find which configuration this room belongs to
      for (const config of doubleConfigs) {
        if (index < roomConfigIndex + config.rooms) {
          guestsForThisRoom = config.guests;
          break;
        }
        roomConfigIndex += config.rooms;
      }

      // If config says 1 guest, flatten to just the firstBed guest
      if (guestsForThisRoom === 1) {
        return toClientTeamMemberInput(firstBed);
      }

      // Otherwise, keep the firstBed/secondBed structure
      return {
        firstBed: toClientTeamMemberInput(firstBed),
        ...(secondBed && { secondBed: toClientTeamMemberInput(secondBed) }),
      };
    });

    return {
      single: mappedSingle as FormValues['single'],
      double: mappedDouble as FormValues['double'],
      suite: mappedSuite as FormValues['suite'],
    };
  }, [requestData]);

  const rooms = requestData.quantities;
  const [updateGroupReservationRequest, { loading }] =
    useUpdateGroupReservationRequestMutation({
      onCompleted: (data) => {
        popToast(
          `Request ${data.updateGroupReservationRequest.id} successfully updated!`,
        );
      },
      onError: (error) => {
        popToast(`Error submitting request: ${error.message}`, {
          type: 'error',
        });
      },
    });

  const showRemarksList =
    request && request.remarks && request.remarks.length > 0;
  const [createRemark] = useCreateRosterRequestRemarkMutation({
    onCompleted: () => {
      popToast(`Remark sent!`);
    },
    onError: (error) => {
      popToast(error.message, { type: 'error' });
    },
    refetchQueries: 'active',
  });

  const submitRemark: RequestRemarksFormProps['onSubmit'] = (values, form) => {
    if (request?.id) {
      createRemark({
        variables: {
          rosterRequestId: request.id,
          input: values,
        },
      });
      form.reset();
    } else {
      popToast('Missing Request', { type: 'error' });
    }
  };
  const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
    const { single, double, suite } = values;

    // https://final-form.org/docs/final-form/faq.  Since "" and undefined is
    // falsy, i opted to use !! to filter for the object and this plays nicely
    // with TS
    const nextSingle = single
      .filter((v): v is NonNullable<typeof v> => !!v)
      .map(toClientTeamMemberInput);
    const nextSuite = suite
      .filter((v): v is NonNullable<typeof v> => !!v)
      .map(toClientTeamMemberInput);
    const nextDouble = double
      .filter((v): v is NonNullable<typeof v> => !!v)
      .map((room) => {
        if ('firstBed' in room) {
          const firstBedGuest = room.firstBed;
          const secondBedGuest = room.secondBed;

          // Normalize: ensure firstBed is populated if any guest exists
          if (!firstBedGuest && secondBedGuest) {
            return {
              firstBed: toClientTeamMemberInput(secondBedGuest),
            };
          } else if (firstBedGuest && secondBedGuest) {
            return {
              firstBed: toClientTeamMemberInput(firstBedGuest),
              secondBed: toClientTeamMemberInput(secondBedGuest),
            };
          } else if (firstBedGuest) {
            return {
              firstBed: toClientTeamMemberInput(firstBedGuest),
            };
          }
          return null;
        } else {
          // Single guest - first bed only
          return {
            firstBed: toClientTeamMemberInput(room as ClientTeamMemberOption),
          };
        }
      })
      .filter((room): room is NonNullable<typeof room> => room !== null);

    void updateGroupReservationRequest({
      variables: {
        id,
        input: {
          single: nextSingle,
          double: nextDouble,
          suite: nextSuite,
          roomConfigurations: requestData.roomConfigurations
            ? removeTypename(requestData.roomConfigurations)
            : undefined,
        },
      },
    })
      .then((result) => {
        if (
          !result.data?.updateGroupReservationRequest ||
          result.errors?.length
        )
          return;

        // The mutation does not return guest fields to refresh the cache. Use
        // the confirmed submission as the baseline, preserving any newer edits.
        const currentValues = form.getState().values;
        form.batch(() => {
          form.initialize(values);
          form.change('single', currentValues.single);
          form.change('double', currentValues.double);
          form.change('suite', currentValues.suite);
        });
      })
      .catch(() => {
        // The mutation's onError handler already displays the failure.
      });
  };

  const onUpdateRequiredFields = (requiredFields: RequiredFieldInput[]) => {
    updateGroupReservationRequest({
      variables: {
        id,
        input: {
          single: requestData.single.map(toClientTeamMemberInput),
          double: requestData.double.map((doubleRoom) => ({
            firstBed: toClientTeamMemberInput(doubleRoom.firstBed),
            secondBed: toClientTeamMemberInput(doubleRoom.secondBed),
          })),
          suite: requestData.suite.map(toClientTeamMemberInput),
          requiredFields,
        },
      },
      update: (cache, { data }) => {
        if (data?.updateGroupReservationRequest) {
          const updatedRequest = data.updateGroupReservationRequest;

          cache.modify({
            id: cache.identify({ __typename: 'RosterRequest', id }),
            fields: {
              requestData(existingRequestData) {
                if (
                  updatedRequest.requestData?.__typename ===
                  'RosterRequestDataNewGroupReservation'
                ) {
                  return {
                    ...existingRequestData,
                    requiredFields: updatedRequest.requestData.requiredFields,
                  };
                }
                return existingRequestData;
              },
            },
          });
        }
      },
    });
  };

  if (isOnlyTraveler) {
    return <TravelerAssignmentView request={request} />;
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <div className="space-y-6">
          {request?.status === RosterRequestStatus.Confirmed ? (
            <ConfirmedBooking request={request} requestData={requestData} />
          ) : (
            <>
              <Unconfirmed
                onUpdateRequiredFields={onUpdateRequiredFields}
                requestData={requestData}
                request={request}
                loading={loading}
              />
            </>
          )}
          {/* Remarks Card */}
          <Card className="overflow-visible">
            <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
              Remarks
            </h2>
            <div className="mt-6 space-y-4">
              <RequestRemarksForm onSubmit={submitRemark} />
            </div>
            {showRemarksList && (
              <div className="mt-6 space-y-4">
                {request.remarks.map((remark) => (
                  <div
                    key={remark.id}
                    className="rounded-md border border-gray-200 bg-gray-50 p-4"
                  >
                    <div className="mb-3 text-sm text-gray-900">
                      {remark.note}
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center">
                        <UserBadge size="normal" user={remark.createdBy} />
                      </div>
                      <div>
                        Sent on {format(parseISO(remark.createdAt), 'M/d/yyyy')}
                        at {format(parseISO(remark.createdAt), 'HH:mm')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Card className="overflow-visible">
            <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
              Guest List
            </h2>
            <div className="mt-6">
              <UpdateGroupRequestForm
                initialValues={{
                  single,
                  double,
                  suite,
                }}
                rooms={rooms}
                roomConfigurations={requestData.roomConfigurations || undefined}
                onSubmit={onSubmit}
              />
            </div>
          </Card>
        </div>
      </div>

      <div className="lg:col-span-1">
        <YourGroupRequestCard
          requestId={id}
          requestData={requestData}
          requestStatus={requestStatus}
        />
      </div>
    </div>
  );
};

export default NewGroupReservationRequest;
